import { ListChecks } from "lucide-react";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge,
  Diagram, MockField, MockSelect, MockButton, MockRow,
} from "../shared";

export default function FormsSubTaskSection() {
  return (
    <div data-testid="guide-forms-sub-task">
      <PageHeading
        icon={ListChecks}
        title="Create / Complete a Sub-Task"
        description="Split a big task into smaller pieces, each with its own person, instruction, and deadline. People then tick off their piece when it's done."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="admin" />
        <span className="text-xs text-white/40">Admins create and delete sub-tasks. Members can complete a sub-task assigned to them.</span>
      </div>

      <Callout variant="info" title="In plain words">
        A sub-task is a <span className="text-white">to-do under a bigger task</span>. Several people can work on their own pieces at the same time, and the main task shows a little "2/5 done" tally.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>When one task is really several jobs that can run in parallel.</li>
          <li>When different people each own a part of the work.</li>
        </ul>
      </Section>

      <Section title="Creating a sub-task — what it looks like">
        <Diagram
          frameLabel="Action Item Detail → Sub-Tasks → Add Sub-Task"
          legend={[
            <>Write the <span className="text-white">instruction</span> — what this piece is.</>,
            <>Pick who it's <span className="text-white">assigned to</span>.</>,
            <>Set the sub-task's own <span className="text-white">due date</span>.</>,
            <>Click <span className="text-white">Add Sub-Task</span> to save it.</>,
          ]}
        >
          <MockField label="Instruction" placeholder="Compile portfolio P&L tables for the last 6 quarters." marker={1} />
          <MockRow>
            <MockSelect label="Assigned to" value="Ravi P." marker={2} />
            <MockField label="Due date" value="24 Jun 2026" marker={3} />
          </MockRow>
          <MockButton label="Add Sub-Task" marker={4} />
        </Diagram>
      </Section>

      <Section title="Step-by-step (creating)">
        <Steps
          steps={[
            <>Open the parent task's detail page and scroll to <span className="text-white">Sub-Tasks</span>, then write the <span className="text-white">instruction</span>.</>,
            <>Pick who it's <span className="text-white">assigned to</span>.</>,
            <>Set the <span className="text-white">due date</span>.</>,
            <>Click <span className="text-white">Add Sub-Task</span>.</>,
          ]}
        />
      </Section>

      <Section title="Every box explained">
        <Field name="Instruction" required="required">
          <p>What this specific sub-task is. It should be doable on its own, separately from the other sub-tasks.</p>
          <p className="text-white/40">Example: "Compile portfolio P&amp;L tables for the last 6 quarters and validate against accounting."</p>
        </Field>

        <Field name="Assigned to" required="required">
          <p>Who should do this sub-task. Pick from team members. Each sub-task can go to a different person.</p>
        </Field>

        <Field name="Due date" required="required">
          <p>The sub-task's own deadline. Usually earlier than the parent task's due date.</p>
        </Field>

        <Field name="Starting status" required="optional">
          <p>Defaults to Pending. Set it to In Progress straight away if work has already begun.</p>
        </Field>
      </Section>

      <NextStep>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>The sub-task appears under the parent task.</li>
          <li>The person assigned gets an email with the parent context and their specific instruction.</li>
          <li>On the Updates page, the parent task shows a small <span className="text-white">"0/N done"</span> chip.</li>
          <li>Event <span className="text-white">SUBTASK_CREATED</span> is recorded on the parent.</li>
        </ul>
      </NextStep>

      <Section title="Completing a sub-task — what it looks like">
        <Diagram
          frameLabel="Updates → your sub-task"
          legend={[
            <>Find your sub-task and click <span className="text-white">Mark Complete</span>.</>,
            <>Optionally add a <span className="text-white">completion note</span>.</>,
          ]}
        >
          <MockButton label="Mark Complete" marker={1} />
          <MockField label="Completion note (optional)" placeholder="Tables compiled and reconciled — see attached." marker={2} />
        </Diagram>
      </Section>

      <Section title="Step-by-step (completing)">
        <Steps
          steps={[
            <>Open the Updates page (or the parent task's detail page), find your sub-task, and click <span className="text-emerald-300">Mark Complete</span>.</>,
            <>Optionally add a <span className="text-white">completion note</span>.</>,
          ]}
        />
        <NextStep>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>The sub-task status moves to <span className="text-emerald-300">Complete</span>.</li>
            <li>The parent task's owner gets an email.</li>
            <li>The "X/N done" chip on the parent updates.</li>
            <li>Event <span className="text-white">SUBTASK_COMPLETED</span> is recorded.</li>
          </ul>
        </NextStep>
      </Section>

      <Callout variant="warning" title="Deleting a sub-task">
        Admins can delete a sub-task — but only if no work has been logged against it (no progress notes, no status moves beyond Pending). This protects the record. If you need to abandon work that's already started, mark it complete with an explanatory note instead.
      </Callout>
    </div>
  );
}
